import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
    Container, Box, Typography, Avatar, Paper, Grid, Chip,
    CircularProgress, Button, Divider, useTheme, useMediaQuery,
} from '@mui/material';
import {
    Bolt as BoltIcon, Share as ShareIcon, EmojiEvents as TrophyIcon,
    Speed as SpeedIcon, MenuBook as BookIcon, TrendingUp as TrendingIcon,
    LocalFireDepartment as FireIcon, Person as PersonIcon,
} from '@mui/icons-material';
import { flexProfileAPI } from '../services/api';
import { trackFlexEvent, FLEX_EVENTS } from '../utils/flexAnalytics';

const FlexProfile = () => {
    const { handle } = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        async function fetchProfile() {
            try {
                setLoading(true);
                const data = await flexProfileAPI.getPublicProfile(handle);
                setProfile(data);
                trackFlexEvent(FLEX_EVENTS.PROFILE_VIEWED, { handle });
            } catch (err) {
                setError(err.response?.data?.error || 'Profile not found.');
            } finally {
                setLoading(false);
            }
        }
        fetchProfile();
    }, [handle]);

    if (loading) {
        return (
            <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
                <PersonIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h5" gutterBottom>{error}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    This profile may be private or does not exist.
                </Typography>
                <Button variant="contained" href="/">
                    Go to ReaderFLEX
                </Button>
            </Container>
        );
    }

    const ogTitle = `@${profile.handle} | ReaderFLEX`;
    const ogDescription = profile.bio || 'ReaderFLEX member';

    return (
        <>
            <Helmet>
                <title>{ogTitle}</title>
                <meta name="description" content={ogDescription} />
                <meta property="og:title" content={ogTitle} />
                <meta property="og:description" content={ogDescription} />
                <meta property="og:type" content="profile" />
                {profile.avatarUrl && <meta property="og:image" content={profile.avatarUrl} />}
                {!profile.profileIndexable && <meta name="robots" content="noindex,nofollow" />}
            </Helmet>

            <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
                {/* Profile Header */}
                <Paper elevation={0} sx={{
                    p: { xs: 2, sm: 4 },
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.divider}`,
                    mb: 3,
                }}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'center', sm: 'flex-start' },
                        gap: 3,
                    }}>
                        <Avatar
                            src={profile.avatarUrl}
                            sx={{
                                width: { xs: 80, sm: 120 },
                                height: { xs: 80, sm: 120 },
                                border: `3px solid ${theme.palette.primary.main}`,
                            }}
                        >
                            {profile.handle?.[0]?.toUpperCase()}
                        </Avatar>
                        <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
                            <Typography variant="h4" sx={{ fontWeight: 800 }}>
                                @{profile.handle}
                            </Typography>
                            {profile.bio && (
                                <Typography variant="body1" color="text.secondary" sx={{ mt: 1, maxWidth: 500 }}>
                                    {profile.bio}
                                </Typography>
                            )}
                            <Box sx={{
                                display: 'flex',
                                gap: 1,
                                mt: 2,
                                flexWrap: 'wrap',
                                justifyContent: { xs: 'center', sm: 'flex-start' },
                            }}>
                                {profile.joinedDate && (
                                    <Chip
                                        size="small"
                                        label={`Joined ${new Date(profile.joinedDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}`}
                                        variant="outlined"
                                    />
                                )}
                                {profile.preferredLanguages?.length > 0 && (
                                    <Chip
                                        size="small"
                                        label={profile.preferredLanguages.map(l => l.toUpperCase()).join(' · ')}
                                        variant="outlined"
                                    />
                                )}
                            </Box>
                        </Box>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ShareIcon />}
                            onClick={() => {
                                navigator.clipboard.writeText(window.location.href);
                            }}
                            sx={{ flexShrink: 0 }}
                        >
                            Share
                        </Button>
                    </Box>
                </Paper>

                {/* Stats Grid */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    {profile.bestWPM !== undefined && (
                        <Grid item xs={6} sm={3}>
                            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
                                <SpeedIcon sx={{ color: '#FFD700', mb: 1 }} />
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>{profile.bestWPM}</Typography>
                                <Typography variant="caption" color="text.secondary">Best WPM</Typography>
                            </Paper>
                        </Grid>
                    )}
                    {profile.averageWPM !== undefined && (
                        <Grid item xs={6} sm={3}>
                            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
                                <TrendingIcon sx={{ color: '#00BFFF', mb: 1 }} />
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>{profile.averageWPM}</Typography>
                                <Typography variant="caption" color="text.secondary">Avg WPM</Typography>
                            </Paper>
                        </Grid>
                    )}
                    {profile.averageComprehension !== undefined && (
                        <Grid item xs={6} sm={3}>
                            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
                                <BookIcon sx={{ color: '#8DC63F', mb: 1 }} />
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>{profile.averageComprehension}%</Typography>
                                <Typography variant="caption" color="text.secondary">Comprehension</Typography>
                            </Paper>
                        </Grid>
                    )}
                    {profile.currentStreak !== undefined && (
                        <Grid item xs={6} sm={3}>
                            <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
                                <FireIcon sx={{ color: '#FF9800', mb: 1 }} />
                                <Typography variant="h5" sx={{ fontWeight: 700 }}>{profile.currentStreak}</Typography>
                                <Typography variant="caption" color="text.secondary">Day Streak</Typography>
                            </Paper>
                        </Grid>
                    )}
                </Grid>

                {/* FLEX Stats */}
                <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <BoltIcon sx={{ color: '#FFD700' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            FLEX Activity
                        </Typography>
                    </Box>
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>{profile.flexStats?.cardsGenerated || 0}</Typography>
                                <Typography variant="caption" color="text.secondary">Cards Generated</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>{profile.flexStats?.cardsShared || 0}</Typography>
                                <Typography variant="caption" color="text.secondary">Cards Shared</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>{profile.flexStats?.challengesCreated || 0}</Typography>
                                <Typography variant="caption" color="text.secondary">Challenges Sent</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>{profile.flexStats?.challengesWon || 0}</Typography>
                                <Typography variant="caption" color="text.secondary">Challenges Won</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Challenge Record */}
                {(profile.challengeRecord?.wins > 0 || profile.challengeRecord?.losses > 0 || profile.challengeRecord?.ties > 0) && (
                    <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <TrophyIcon sx={{ color: '#FFD700' }} />
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                Challenge Record
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center' }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>{profile.challengeRecord.wins}</Typography>
                                <Typography variant="caption" color="text.secondary">Wins</Typography>
                            </Box>
                            <Divider orientation="vertical" flexItem />
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.secondary' }}>{profile.challengeRecord.ties}</Typography>
                                <Typography variant="caption" color="text.secondary">Ties</Typography>
                            </Box>
                            <Divider orientation="vertical" flexItem />
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h5" sx={{ fontWeight: 700, color: 'error.main' }}>{profile.challengeRecord.losses}</Typography>
                                <Typography variant="caption" color="text.secondary">Losses</Typography>
                            </Box>
                        </Box>
                    </Paper>
                )}

                {/* Card Gallery */}
                {profile.cards?.length > 0 && (
                    <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                            FLEX Card Gallery
                        </Typography>
                        <Grid container spacing={2}>
                            {profile.cards.map((card) => (
                                <Grid item xs={12} sm={6} md={4} key={card._id}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            borderRadius: 2,
                                            border: `1px solid ${theme.palette.divider}`,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            '&:hover': { borderColor: 'primary.main', transform: 'translateY(-2px)' },
                                        }}
                                        onClick={() => window.open(`/flex/card/${card._id}`, '_blank')}
                                    >
                                        <Chip
                                            label={card.cardType}
                                            size="small"
                                            sx={{ mb: 1, textTransform: 'capitalize' }}
                                        />
                                        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                                            {card.headline}
                                        </Typography>
                                        {card.subheadline && (
                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                {card.subheadline}
                                            </Typography>
                                        )}
                                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                                            {new Date(card.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                )}

                {/* CTA for visitors */}
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Want to challenge @{profile.handle} or build your own reading stats?
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        href={`/?ref=${profile.referralCode || profile.handle}`}
                        sx={{
                            background: 'linear-gradient(135deg, #FFD700 0%, #FF9800 100%)',
                            color: '#0D1421',
                            fontWeight: 700,
                        }}
                    >
                        Join ReaderFLEX
                    </Button>
                </Box>
            </Container>
        </>
    );
};

export default FlexProfile;
